// api/adminApi.ts
import { AdminUpdateUserRequest, AppStats } from "../types/adminTypes";
import {
  Company,
  CreateCompanyReq,
  CreateUserCompanyReq,
} from "../types/companyTypes";
import { baseApi } from "./BaseApi";
import {
  CompanyRole,
  Employee,
  User,
  WaitingListEntry,
} from "../types/commonTypes";
import {
  Job,
  UpdateJobReq,
  UpdateJobAssignmentReq,
  AdminJobSummary,
  ActiveJob,
} from "../types/jobTypes";
import Products from "../../admin_dashboard/src/pages/Products/Products";
import { JobStatus } from "../types/jobTypes";
import { Affiliate, CreateAffiliateRequest } from "../types/affiliateTypes";
import {
  SecurityOption,
  CreateProductReq,
  UpdateProductReq,
  SecurityOptionFull,
  OptionPricing,
} from "../types/productTypes";
import { DateTime } from "luxon";

interface ApiJob extends Omit<Job, "startTime"> {
  startTime: string;
}

interface ApiAdminJobSummary extends Omit<AdminJobSummary, "newJobs"> {
  activeJobs: ActiveJob[];
  newJobs: ApiJob[];
}
const adminApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    syncCognitoUsers: builder.mutation<any, void>({
      query: () => ({
        url: `/_admin/cognito/sync`,
        method: "POST",
      }),
    }),

    appStats: builder.query<AppStats, void>({
      query: () => ({
        url: "/_admin/stats",
      }),
    }),

    adminGetCompanies: builder.query<Company[], void>({
      query: () => `/_admin/companies`,
      providesTags: ["AdminCompanies"],
    }),

    adminCreateCompany: builder.mutation<Company, CreateCompanyReq>({
      query: (req) => ({
        url: `/_admin/companies`,
        method: "POST",
        body: req,
      }),
      invalidatesTags: ["AdminCompanies"],
    }),

    allUsers: builder.query<User[], void>({
      query: () => `/_admin/users`,
    }),

    adminGetUser: builder.query<User, string>({
      query: (arg) => `/_admin/users/${arg}`,
      providesTags: ["AdminUserDetails"],
    }),

    adminUpdateUser: builder.mutation<
      User,
      { userId: string; request: AdminUpdateUserRequest }
    >({
      query: ({ userId, request }) => ({
        url: `/_admin/users/${userId}`,
        body: request,
        method: "PUT",
      }),
      invalidatesTags: ["AdminUserDetails", "AdminJobsSummary", "JobDetails"],
    }),

    deleteUserValidationImage: builder.mutation<
      void,
      { userId: string; fileId: number }
    >({
      query: ({ userId, fileId }) => ({
        url: `/_admin/users/${userId}/validation-file/${fileId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["AdminUserDetails"],
    }),

    // Company

    adminGetCompanyEmployees: builder.query<Employee[], number>({
      query: (arg) => `_admin/companies/${arg}/employees`,
      providesTags: ["CompanyEmployees"],
    }),

    adminAddEmployeesToCompany: builder.mutation<
      void,
      { companyId: number; body: { userIds: string[]; role: CompanyRole } }
    >({
      query: (arg) => ({
        url: `/_admin/companies/${arg.companyId}/employees`,
        method: "POST",
        body: arg.body,
      }),
      invalidatesTags: ["CompanyEmployees"],
    }),

    adminRemoveEmployeeFromCompany: builder.mutation<
      void,
      { companyId: number; userId: string }
    >({
      query: (arg) => ({
        url: `/_admin/companies/${arg.companyId}/employees/${arg.userId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["CompanyEmployees"],
    }),

    // Jobs
    adminGetJobs: builder.query<Job[], { statusFilter?: JobStatus[] }>({
      query: (params) =>
        `/_admin/jobs${
          params.statusFilter && `?statuses=${params.statusFilter.join(",")}`
        }`,
      transformResponse: (response: ApiJob[]): Job[] => {
        return response.map((apiJob) => ({
          ...apiJob,
          startTime: DateTime.fromISO(apiJob.startTime, {
            zone: "utc",
          }).setZone("America/Los_Angeles"),
        }));
      },
    }),

    admingGetJobsSummary: builder.query<AdminJobSummary, void>({
      query: () => "/_admin/jobs/summary",
      transformResponse: (response: ApiAdminJobSummary): AdminJobSummary => ({
        ...response,
        newJobs: response.newJobs.map((apiJob) => ({
          ...apiJob,
          startTime: DateTime.fromISO(apiJob.startTime, {
            zone: "utc",
          }).setZone("America/Los_Angeles"),
        })),
      }),
      providesTags: ["AdminJobsSummary"],
    }),

    adminGetJobById: builder.query<Job, number>({
      query: (arg) => ({
        url: `/_admin/jobs/${arg}`,
      }),
      transformResponse: (response: ApiJob): Job => ({
        ...response,
        startTime: DateTime.fromISO(response.startTime, {
          zone: "utc",
        }).setZone("America/Los_Angeles"),
      }),
      providesTags: ["JobDetails"],
    }),

    adminUpdateJob: builder.mutation<
      void,
      { jobId: number; body: UpdateJobReq }
    >({
      query: (arg) => ({
        url: `/_admin/jobs/${arg.jobId}`,
        method: "PUT",
        body: arg.body,
      }),
      invalidatesTags: ["JobDetails"],
    }),

    adminUpdateJobAssignment: builder.mutation<
      void,
      { jobId: number; body: UpdateJobAssignmentReq }
    >({
      query: (arg) => ({
        url: `/_admin/jobs/${arg.jobId}/assignments`,
        method: "PUT",
        body: arg.body,
      }),
      invalidatesTags: ["JobDetails"],
    }),

    // Products
    adminGetProducts: builder.query<SecurityOptionFull[], void>({
      query: () => `/_admin/products`,
    }),

    adminGetProductById: builder.query<SecurityOptionFull, number>({
      query: (productId) => `/_admin/products/${productId}`,
      providesTags: ["ProductDetails"],
    }),

    adminCreateProduct: builder.mutation<SecurityOptionFull, CreateProductReq>({
      query: (arg) => ({
        url: `/_admin/products`,
        method: "POST",
        body: arg,
      }),
    }),

    adminUpdateProduct: builder.mutation<
      void,
      { productId: number; body: UpdateProductReq }
    >({
      query: (args) => ({
        url: `/_admin/products/${args.productId}`,
        method: "PUT",
        body: args.body,
      }),
    }),

    adminCreateProductPricing: builder.mutation<
      OptionPricing,
      { productId: number; priceInCents: number }
    >({
      query: ({ productId, ...body }) => ({
        url: `/_admin/products/${productId}/pricing`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["ProductDetails"],
    }),

    adminSetProductPricingActive: builder.mutation<
      void,
      { productId: number; optionPricingId: number }
    >({
      query: ({ productId, optionPricingId }) => ({
        url: `/_admin/products/${productId}/pricing/${optionPricingId}/activate`,
        method: "PUT",
      }),
      invalidatesTags: ["ProductDetails"],
    }),

    // Affiliates
    adminGetAffiliates: builder.query<Affiliate[], void>({
      query: () => `/_admin/affiliates`,
      providesTags: ["Affiliates"],
    }),

    adminGetAffiliateById: builder.query<Affiliate, number>({
      query: (affiliateId) => `/_admin/affiliates/${affiliateId}`,
    }),

    adminCreateAffiliate: builder.mutation<Affiliate, CreateAffiliateRequest>({
      query: (arg) => ({
        url: `/_admin/affiliates`,
        method: "POST",
        body: arg,
      }),
      invalidatesTags: ["Affiliates"],
    }),
    getWaitingList: builder.query<WaitingListEntry[], void>({
      query: () => `/_admin/waiting-list`,
    }),
  }),
});

export const {
  useSyncCognitoUsersMutation,
  useAppStatsQuery,
  useAdminGetCompaniesQuery,
  useAdminCreateCompanyMutation,
  useLazyAllUsersQuery,
  useAdminAddEmployeesToCompanyMutation,
  useAdminRemoveEmployeeFromCompanyMutation,
  useAdminGetUserQuery,
  useAdminGetJobByIdQuery,
  useAdminGetJobsQuery,
  useLazyAdminGetCompaniesQuery,
  useLazyAdminGetCompanyEmployeesQuery,
  useAdminUpdateJobAssignmentMutation,
  useAdminUpdateJobMutation,
  useAdminGetProductsQuery,
  useAdminCreateProductMutation,
  useAdminUpdateProductMutation,
  useAdminGetProductByIdQuery,
  useDeleteUserValidationImageMutation,
} = adminApi;

export default adminApi;
